import { ReactElement, SyntheticEvent, useRef } from 'react';

import { AccessTextField } from '@changegps/horizon-component-library';
import { styled, TextFieldProps } from '@mui/material';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';
import { NumericFormat, NumericFormatProps } from 'react-number-format';

import { removeTheseProps } from '@/utils';

type CustomTextFieldProps = TextFieldProps & {
  leftAlignValue?: boolean;
};

const CustomTextField = styled(AccessTextField, {
  shouldForwardProp: removeTheseProps<CustomTextFieldProps>('leftAlignValue'),
})<CustomTextFieldProps>(({ leftAlignValue }) => ({
  '& .MuiInputBase-input': {
    textAlign: leftAlignValue ? 'unset' : 'right',
  },
}));

export type AccessNumericFormatProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  ControllerProps: Omit<ControllerProps<TFieldValues, TName>, 'render'>;
} & Omit<NumericFormatProps, 'customInput' | 'onValueChange' | 'onChange'> &
  Omit<CustomTextFieldProps, 'InputProps' | 'onChange'> & {
    InputProps?: Omit<CustomTextFieldProps['InputProps'], 'inputRef'>;
    onChange?: (
      event: SyntheticEvent<HTMLInputElement, Event> | undefined,
      valueToSave: number | null,
    ) => void;
    readOnly?: boolean;
  };
export const AccessNumericFormat = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ControllerProps,
  onChange: componentOnChange,
  readOnly,
  ...props
}: AccessNumericFormatProps<TFieldValues, TName>): ReactElement => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Controller
      {...ControllerProps}
      render={({
        field: { ref, onChange, ...field },
        fieldState: { error },
      }) => {
        return (
          <NumericFormat
            {...field}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator
            {...props}
            error={props.error || Boolean(error)}
            helperText={error?.message || props.helperText}
            InputProps={{
              readOnly,
              ...props.InputProps,
              inputRef: (e: HTMLInputElement) => {
                inputRef.current = e;
                ref(e);
              },
            }}
            customInput={CustomTextField}
            onValueChange={({ floatValue }, { event }) => {
              const valueToSave =
                typeof floatValue === 'undefined' ? null : floatValue;
              onChange(valueToSave);
              componentOnChange && componentOnChange(event, valueToSave);
            }}
            onFocus={() => {
              if (!inputRef.current || field.value > 0) return;
              if (props.prefix === '$') inputRef.current.value = '$';
              if (props.prefix === '%' || props.suffix === '%')
                inputRef.current.value = '%';
            }}
            onBlur={(event) => {
              props.onBlur && props.onBlur(event);

              if (!inputRef.current) return;
              const value = event.target.value;

              if (props.prefix === '$' && (!value || value.trim() === '$')) {
                onChange(0);
                inputRef.current.value = '$0.00';
              }

              if (
                props.prefix === '%' ||
                (props.suffix === '%' && (!value || value.trim() === '%'))
              ) {
                onChange(0);
                inputRef.current.value === '0%';
              }
            }}
          />
        );
      }}
    />
  );
};
